import React, { useState, useEffect, useRef } from 'react';
import { DndContext, closestCorners, PointerSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';
import { useDroppable, useDraggable } from '@dnd-kit/core';
import { Plus, Building2, User, GripVertical, X, Edit, Search, UserPlus, Trash2, Link2, Copy, ExternalLink, AlertTriangle, ChevronDown } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'react-toastify';

function ContactCard({ contact }) {
  return (
    <div className="flex items-center gap-2 bg-white rounded-lg p-2.5 border border-slate-200 shadow-sm">
      <div className="text-slate-300"><GripVertical size={14} /></div>
      <div className="w-7 h-7 bg-gradient-to-br from-orange-400 to-rose-500 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
        {contact.FullName?.charAt(0)}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-slate-700 truncate">{contact.FullName}</p>
        <p className="text-[10px] text-slate-400 truncate">{contact.Email}</p>
      </div>
    </div>
  );
}

function DraggableContact({ contact, onDelete }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `contact-${contact.Id}`, data: { contact } });

  return (
    <div className={`flex items-center gap-2 bg-white rounded-lg p-2.5 border border-slate-200 hover:shadow-sm group ${isDragging ? 'opacity-30' : ''}`}>
      <div ref={setNodeRef} {...attributes} {...listeners} className="text-slate-300 hover:text-slate-500 cursor-grab"><GripVertical size={14} /></div>
      <div className="w-7 h-7 bg-gradient-to-br from-orange-400 to-rose-500 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
        {contact.FullName?.charAt(0)}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-slate-700 truncate">{contact.FullName}</p>
        <p className="text-[10px] text-slate-400 truncate">{contact.Email}</p>
      </div>
      <button onClick={() => onDelete(contact.Id)} className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-red-600 transition-all" title="Kalıcı sil">
        <Trash2 size={13} />
      </button>
    </div>
  );
}

function CustomerDropZone({ customer, contacts, onRemoveContact, onDeleteContact, onGenerateToken }) {
  const { setNodeRef, isOver } = useDroppable({ id: `customer-${customer.Id}` });
  const supportUrl = customer.SupportToken ? `${window.location.origin}/support/${customer.SupportToken}` : null;

  return (
    <div ref={setNodeRef} className={`bg-white rounded-xl shadow-sm border p-4 transition-all ${isOver ? 'ring-2 ring-blue-400 border-blue-300 bg-blue-50/30' : 'border-slate-200'}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center text-white text-sm font-bold">
            {customer.Name.charAt(0)}
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-800">{customer.Name}</h3>
            {customer.Email && <p className="text-xs text-slate-400">{customer.Email}</p>}
          </div>
        </div>
        <span className="text-xs font-medium text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">{contacts.length} kişi</span>
      </div>
      {/* Destek Linki */}
      {supportUrl ? (
        <div className="mb-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg px-3 py-2 border border-blue-100">
          <div className="flex items-center gap-1.5 mb-1">
            <Link2 size={12} className="text-blue-600" />
            <span className="text-[10px] font-semibold text-blue-700">Destek Linki</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-blue-600 truncate flex-1 font-mono bg-white/60 px-2 py-1 rounded">{supportUrl}</span>
            <button onClick={() => { navigator.clipboard.writeText(supportUrl); toast.success('Destek linki kopyalandı!'); }}
              className="p-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors" title="Kopyala">
              <Copy size={12} />
            </button>
            <a href={supportUrl} target="_blank" rel="noopener noreferrer"
              className="p-1.5 bg-slate-100 text-slate-600 rounded-md hover:bg-slate-200 transition-colors" title="Aç">
              <ExternalLink size={12} />
            </a>
          </div>
        </div>
      ) : (
        <button onClick={() => onGenerateToken(customer.Id)}
          className="mb-3 w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-50 text-blue-600 border border-blue-200 rounded-lg text-xs font-medium hover:bg-blue-100 transition-colors">
          <Link2 size={14} /> Destek Linki Oluştur
        </button>
      )}
      <div className="space-y-1.5 min-h-[60px]">
        {contacts.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-4">Henüz çalışan yok</p>
        ) : contacts.map(c => (
          <div key={c.Id} className="flex items-center gap-2 bg-slate-50 rounded-lg p-2 group">
            <div className="w-6 h-6 bg-gradient-to-br from-orange-400 to-rose-500 rounded-full flex items-center justify-center text-white text-[10px] font-bold">
              {c.FullName?.charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-slate-700 truncate">{c.FullName}</p>
              <p className="text-[10px] text-slate-400 truncate">{c.Email}</p>
            </div>
            <button onClick={() => onRemoveContact(customer.Id, c.Id)} className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-orange-500 transition-all" title="Müşteriden çıkar">
              <X size={14} />
            </button>
            <button onClick={() => onDeleteContact(c.Id)} className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-red-600 transition-all" title="Kalıcı sil">
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [unassignedContacts, setUnassignedContacts] = useState([]);
  const [customerContacts, setCustomerContacts] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', address: '' });
  const [contactForm, setContactForm] = useState({ email: '', fullName: '', phone: '', customerId: '' });
  const [search, setSearch] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [activeContact, setActiveContact] = useState(null);
  const [deleteContactDialog, setDeleteContactDialog] = useState(null);
  const [contactCustomerSearch, setContactCustomerSearch] = useState('');
  const [showContactCustomerDropdown, setShowContactCustomerDropdown] = useState(false);
  const contactCustomerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (contactCustomerRef.current && !contactCustomerRef.current.contains(e.target)) setShowContactCustomerDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [custRes, unassRes, allContactsRes] = await Promise.all([
        api.get('/customers'),
        api.get('/customers/contacts/unassigned'),
        api.get('/customers/contacts')
      ]);
      setCustomers(custRes.data);
      setUnassignedContacts(unassRes.data);

      // Çalışanları müşteriye göre grupla
      const contactsMap = {};
      for (const c of allContactsRes.data) {
        if (c.CustomerId) {
          if (!contactsMap[c.CustomerId]) contactsMap[c.CustomerId] = [];
          contactsMap[c.CustomerId].push(c);
        }
      }
      setCustomerContacts(contactsMap);
    } catch (err) {}
    setLoading(false);
  };

  const handleDragStart = (event) => {
    setActiveContact(event.active.data.current?.contact || null);
  };

  const handleDragEnd = async (event) => {
    setActiveContact(null);
    const { active, over } = event;
    if (!over) return;

    const contactId = active.data.current?.contact?.Id;
    const customerId = parseInt(over.id.replace('customer-', ''));

    if (!contactId || !customerId) return;

    try {
      await api.post(`/customers/${customerId}/assign-contact`, { contactId });
      toast.success('Çalışan müşteriye bağlandı!');
      loadAll();
    } catch (err) {
      toast.error('Bağlama başarısız');
    }
  };

  const handleRemoveContact = async (customerId, contactId) => {
    try {
      await api.post(`/customers/${customerId}/remove-contact`, { contactId });
      toast.success('Çalışan müşteriden çıkarıldı');
      loadAll();
    } catch (err) {
      toast.error('İşlem başarısız');
    }
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await api.delete(`/customers/contacts/${contactId}`);
      toast.success('Çalışan kalıcı olarak silindi');
      setDeleteContactDialog(null);
      loadAll();
    } catch (err) {
      if (err.response?.status === 409) {
        setDeleteContactDialog({ contactId, tickets: err.response.data.tickets });
      } else {
        toast.error(err.response?.data?.error || 'Silme başarısız');
      }
    }
  };

  const handleGenerateToken = async (customerId) => {
    try {
      await api.post(`/customers/${customerId}/generate-token`);
      toast.success('Destek linki oluşturuldu!');
      loadAll();
    } catch (err) {
      toast.error('Link oluşturulamadı');
    }
  };

  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    try {
      await api.post('/customers', form);
      toast.success('Müşteri oluşturuldu!');
      setForm({ name: '', email: '', phone: '', address: '' });
      setShowForm(false);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Hata');
    }
  };

  const handleCreateContact = async (e) => {
    e.preventDefault();
    if (!contactForm.customerId) { toast.error('Müşteri firma seçimi zorunludur!'); return; }
    try {
      await api.post('/customers/contacts', contactForm);
      toast.success('Çalışan eklendi!');
      setContactForm({ email: '', fullName: '', phone: '', customerId: '' });
      setContactCustomerSearch('');
      setShowContactForm(false);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Hata');
    }
  };

  const filteredContactCustomers = customers.filter(c =>
    !contactCustomerSearch || c.Name.toLowerCase().includes(contactCustomerSearch.toLowerCase())
  );

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-slate-800">Müşteri Yönetimi</h1>
        <div className="flex gap-2">
          <button onClick={() => setShowContactForm(true)} className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-xl text-sm font-medium hover:bg-orange-600 transition-colors">
            <UserPlus size={18} /> Yeni Çalışan
          </button>
          <button onClick={() => setShowForm(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-colors">
            <Plus size={18} /> Yeni Müşteri
          </button>
        </div>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div>
          {/* Customer Cards */}
          <div>
            <div className="relative mb-4">
              <Search size={16} className="absolute left-3 top-2.5 text-slate-400" />
              <input type="text" value={customerSearch} onChange={e => setCustomerSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Müşteri ara (firma adı, e-posta)..." />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {customers.filter(c => !customerSearch || c.Name.toLowerCase().includes(customerSearch.toLowerCase()) || (c.Email && c.Email.toLowerCase().includes(customerSearch.toLowerCase())))
              .map(customer => (
              <CustomerDropZone key={customer.Id} customer={customer} contacts={customerContacts[customer.Id] || []} onRemoveContact={handleRemoveContact} onDeleteContact={handleDeleteContact} onGenerateToken={handleGenerateToken} />
            ))}
            {customers.length === 0 && (
              <div className="col-span-full text-center py-12">
                <Building2 size={48} className="text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Henüz müşteri yok</p>
              </div>
            )}
            </div>
          </div>
        </div>
        <DragOverlay>
          {activeContact ? <ContactCard contact={activeContact} /> : null}
        </DragOverlay>
      </DndContext>

      {/* Create Customer Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-slate-800">Yeni Müşteri (Firma)</h3>
              <button onClick={() => setShowForm(false)} className="p-1 text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>
            <form onSubmit={handleCreateCustomer} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Firma Adı *</label>
                <input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">E-posta</label>
                <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Telefon</label>
                  <input type="text" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Adres</label>
                  <input type="text" value={form.address} onChange={e => setForm({...form, address: e.target.value})}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <button type="submit" className="w-full py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors">
                Müşteri Oluştur
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Create Contact Modal */}
      {showContactForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-slate-800">Yeni Çalışan (Müşteri Personeli)</h3>
              <button onClick={() => setShowContactForm(false)} className="p-1 text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>
            <form onSubmit={handleCreateContact} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Ad Soyad *</label>
                <input type="text" value={contactForm.fullName} onChange={e => setContactForm({...contactForm, fullName: e.target.value})} required
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">E-posta *</label>
                <input type="email" value={contactForm.email} onChange={e => setContactForm({...contactForm, email: e.target.value})} required
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Telefon</label>
                <input type="text" value={contactForm.phone} onChange={e => setContactForm({...contactForm, phone: e.target.value})}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              {/* Müşteri Firma Seçimi */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Müşteri Firma *</label>
                <div className="relative" ref={contactCustomerRef}>
                  <div className="flex items-center border border-slate-200 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-orange-500">
                    <Search size={16} className="ml-3 text-slate-400 flex-shrink-0" />
                    <input type="text" value={contactCustomerSearch} onChange={e => { setContactCustomerSearch(e.target.value); setShowContactCustomerDropdown(true); if (!e.target.value) setContactForm({...contactForm, customerId: ''}); }}
                      onFocus={() => setShowContactCustomerDropdown(true)}
                      className="w-full px-3 py-2.5 focus:outline-none text-sm" placeholder="Firma ara..." />
                    {contactForm.customerId && (
                      <button type="button" onClick={() => { setContactForm({...contactForm, customerId: ''}); setContactCustomerSearch(''); }}
                        className="mr-2 p-1 text-slate-400 hover:text-red-500"><X size={14} /></button>
                    )}
                    <ChevronDown size={16} className="mr-3 text-slate-400 flex-shrink-0" />
                  </div>
                  {showContactCustomerDropdown && (
                    <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-xl max-h-48 overflow-y-auto">
                      {filteredContactCustomers.length === 0 ? (
                        <p className="px-4 py-3 text-sm text-slate-400">Firma bulunamadı</p>
                      ) : filteredContactCustomers.map(c => (
                        <button key={c.Id} type="button" onClick={() => { setContactForm({...contactForm, customerId: String(c.Id)}); setContactCustomerSearch(c.Name); setShowContactCustomerDropdown(false); }}
                          className={`w-full text-left px-4 py-2.5 text-sm hover:bg-orange-50 transition-colors flex items-center gap-2 ${
                            String(c.Id) === contactForm.customerId ? 'bg-orange-50 text-orange-700 font-medium' : 'text-slate-700'
                          }`}>
                          <Building2 size={14} className="text-slate-400 flex-shrink-0" />
                          {c.Name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <button type="submit" className="w-full py-2.5 bg-orange-500 text-white rounded-xl font-medium hover:bg-orange-600 transition-colors">
                Çalışan Ekle
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Contact Delete - Ticket Conflict Dialog */}
      {deleteContactDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center gap-3 px-6 py-4 border-b bg-red-50 rounded-t-2xl">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                <AlertTriangle size={20} className="text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-red-800">Silinemez!</h3>
                <p className="text-sm text-red-600">Bu çalışanın bağlı ticket'ları var</p>
              </div>
              <button onClick={() => setDeleteContactDialog(null)} className="ml-auto p-1 text-red-400 hover:text-red-600"><X size={20} /></button>
            </div>
            <div className="p-6">
              <p className="text-sm text-slate-600 mb-3">Önce aşağıdaki ticket'ları silmeniz gerekiyor:</p>
              <div className="space-y-2 max-h-[250px] overflow-y-auto">
                {deleteContactDialog.tickets.map(t => (
                  <div key={t.Id} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-slate-500">#{t.Id}</span>
                      <span className="text-sm text-slate-700">{t.Title}</span>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                      t.Status === 'Kapatıldı' ? 'bg-slate-100 text-slate-600' :
                      t.Status === 'Çözüldü' ? 'bg-green-100 text-green-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>{t.Status}</span>
                  </div>
                ))}
              </div>
              <button onClick={() => setDeleteContactDialog(null)}
                className="w-full mt-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200 transition-colors text-sm">
                Tamam, Anladım
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
